import { type ClassValue, clsx } from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';

/**
 * Marketing-ui's class merger. Same shape as @repo/ui's cn — kept in
 * this package so the marketing tree doesn't pull @repo/ui (the
 * shadcn-tokenised primitives there share none of our state classes).
 *
 * CON-322: it must also carry the DESIGN.md §1.3 type scale. `text-*`
 * is ambiguous — `text-red-500` is a colour, `text-h3` is a font size —
 * and tailwind-merge only knows Tailwind's own t-shirt sizes, so every
 * other `text-<word>` lands in the catch-all `text-color` group. That
 * made `cn('text-text-heading-1', 'text-h3')` drop the colour, which is
 * how the dashboard's primary buttons shipped black on black.
 *
 * This list mirrors TYPE_SCALE in packages/ui/src/lib/utils.ts, which is
 * asserted against the `--text-*` tokens in styles/marketing.css. Keep
 * the two in step when the scale changes.
 */
const TYPE_SCALE = [
  'h1',
  'h1-tab',
  'h1-mob',
  'h2',
  'h2-tab',
  'h2-mob',
  'h3',
  'h3-tab',
  'h4',
  'h4-tab',
  'h5',
  'h5-tab',
  'h5-mob',
  'h6',
  'h6-tab',
  'h6-mob',
  'body-xs',
  'body-sm',
  'body-sm-tab',
  'body-sm-mob',
  'body-md',
  'body-md-tab',
  'body-md-mob',
  'body-lg',
  'body-lg-tab',
  'body-lg-mob',
  'label-xs',
  'label-xs-tab',
  'label-sm',
  'label-sm-tab',
  'label-md',
  'label-md-tab',
  'label-lg',
  'label-lg-tab',
  'overline',
] as const;

const twMerge = extendTailwindMerge({
  extend: { classGroups: { 'font-size': [{ text: [...TYPE_SCALE] }] } },
});

export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

export { TYPE_SCALE };
